import "./TaskPanelLeft.css";

import { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";

import {
  ArrowRoundRightLightIcon,
  AsteriskButtonIcon,
  EditPencilIcon,
  MilestoneFlagIcon,
  RepeatIcon,
} from "../../../../assets";
import { UserType, WorkflowOrganisationAccessLevel } from "../../../../constants";
import { SelectData, TaskPanelData } from "../../../../models";
import { GetTaskBoardResponse } from "../../../../service/query";
import { ServiceError } from "../../../../service/Shared";
import { hasWorkflowOrganisationAccessLevel } from "../../../../utils";
import { getWorkflowTasksBoardRoute } from "../../../../utils/routes";
import {
  Button,
  Column,
  Divider,
  ErrorBox,
  FormLabel,
  KanaTooltip,
  Row,
  TextArea,
  TextInput,
} from "../../../../widget";

export interface TaskPanelLeftProps {
  errors: ServiceError[] | undefined;
  hasFullEditAccess: boolean;
  workflow: GetTaskBoardResponse["results"][number] | undefined;
  currentUserType: UserType;
  workflowOrganisationAccessLevel: WorkflowOrganisationAccessLevel | undefined;

  activitySelectData: SelectData;

  isCreateTask: boolean;

  isTitleEditable: boolean;
  setIsTitleEditable: Dispatch<SetStateAction<boolean>>;
  currentTask: TaskPanelData;
  setCurrentTask: Dispatch<SetStateAction<TaskPanelData>>;

  handleLinkedActivityOnClick: () => void;
  handleSubmit: () => Promise<void>;
  handleCloseTaskPanel: () => void;
  isHandleSubmitLoading: boolean;
}

export const TaskPanelLeft = ({
  errors,
  hasFullEditAccess,
  workflow,
  currentUserType,
  workflowOrganisationAccessLevel,

  activitySelectData,

  isCreateTask,

  isTitleEditable,
  setIsTitleEditable,
  currentTask,
  setCurrentTask,

  handleLinkedActivityOnClick,
  handleSubmit,
  handleCloseTaskPanel,
  isHandleSubmitLoading,
}: TaskPanelLeftProps): JSX.Element => {
  return (
    <div className="TaskPanelLeft">
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}
      <Row spacingV={isCreateTask || isTitleEditable ? "ml" : "s"}>
        <Column span={12}>
          {isCreateTask || isTitleEditable ? (
            <TextInput
              name="Editable Title"
              label="Name*"
              placeholder="Add task name"
              value={currentTask?.name}
              onChange={(value) => setCurrentTask({ ...currentTask, name: value })}
              maxLength={100}
              displayCharacterCount
            />
          ) : (
            <div className="TaskPanelTitle">
              <h1>{currentTask?.name}</h1>
              {hasFullEditAccess && (
                <Button
                  text=""
                  icon={<EditPencilIcon />}
                  variant="secondary"
                  onClick={() => setIsTitleEditable(true)}
                  minWidth={false}
                  hasNoPadding
                />
              )}
            </div>
          )}
        </Column>
      </Row>
      <Row
        spacingV={
          !hasFullEditAccess && (currentTask.isMandatory || currentTask.isMilestone || currentTask.repeat) ? "m" : "ll"
        }
      >
        <Column span={12}>
          <p className="body1 TaskPanelSubheader">
            {workflow && <Link to={getWorkflowTasksBoardRoute(workflow.uuid, currentUserType)}>{workflow.name}</Link>}
          </p>
        </Column>
      </Row>
      {!hasFullEditAccess && (currentTask.isMandatory || currentTask.isMilestone || currentTask.repeat) && (
        <Row spacingV="ll">
          <Column span={12}>
            <div className="TaskPanelFlags">
              {currentTask.isMandatory && (
                <KanaTooltip tooltipHeader="" tooltipText="Mandatory task" icon={<AsteriskButtonIcon />} />
              )}
              {currentTask.isMilestone && (
                <KanaTooltip
                  tooltipHeader=""
                  tooltipText="Milestone task"
                  icon={<MilestoneFlagIcon className="MilestoneFlagIcon" />}
                />
              )}
              {currentTask.repeat && (
                <KanaTooltip tooltipHeader="" tooltipText="Repeat task" icon={<RepeatIcon className="RepeatIcon" />} />
              )}
            </div>
          </Column>
        </Row>
      )}
      {currentTask.activityUuid && activitySelectData.length > 0 && currentTask.relateToActivity && (
        <Row spacingV="ll">
          <Column span={12}>
            <div className="TaskPanelLinkedActivity">
              <FormLabel label="Linked activity" />
              <Button
                text={activitySelectData.find((activity) => activity.key === currentTask.activityUuid)?.value as string}
                onClick={handleLinkedActivityOnClick}
                icon={<ArrowRoundRightLightIcon />}
                hasNoPadding
                corners="rounded"
                variant="forestGreen"
                size="small"
              />
            </div>
          </Column>
        </Row>
      )}
      <Row spacingV="xl">
        <Column span={12}>
          {hasFullEditAccess ? (
            <TextArea
              name="Description"
              label="Description"
              value={currentTask?.description ?? ""}
              displayCharacterCount
              maxLength={500}
              onChange={(value) => setCurrentTask({ ...currentTask, description: value })}
            />
          ) : (
            <>
              <FormLabel label="Description" />
              <p className="body1">{currentTask?.description ?? ""}</p>
            </>
          )}
        </Column>
      </Row>
      <Row spacingV="xl">
        <Column span={12}>
          <Divider type="horizontal" />
        </Column>
      </Row>
      {hasWorkflowOrganisationAccessLevel(WorkflowOrganisationAccessLevel.User, workflowOrganisationAccessLevel) && (
        <Row>
          <Column span={12}>
            <Button text="Save" onClick={handleSubmit} hasNoPadding isLoading={isHandleSubmitLoading} />
            <Button text="Cancel" variant="secondary" onClick={handleCloseTaskPanel} hasNoPadding />
          </Column>
        </Row>
      )}
    </div>
  );
};
